import {
  DEFAULT_SYSTEM_LOCALE,
  SYSTEM_LOCALES,
  type SystemLocale,
} from '@ts-max/car-export-enum-sdk';

const TENANT_CODE_PATTERN = /^[a-z0-9_-]{1,50}$/;
const CANONICAL_LOCALE_TOKEN_PATTERN = /^[a-z]{2,3}-[A-Z]{2}$/;

export type MobileRouteContext = {
  locale: SystemLocale;
  tenantCode: string;
  routePath: string;
  basename: string;
  canonicalPathname: string;
  needsNormalization: boolean;
};

export type MobileLocationParts = {
  pathname: string;
  search?: string;
  hash?: string;
};

type MobileHistory = Pick<History, 'replaceState' | 'state'>;

export function isSystemLocale(value: string): value is SystemLocale {
  return SYSTEM_LOCALES.includes(value as SystemLocale);
}

function ensurePathname(pathname: string): string {
  if (!pathname) return '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

function isTenantCode(value: string | undefined): value is string {
  return Boolean(value && TENANT_CODE_PATTERN.test(value));
}

function buildBasename(locale: SystemLocale, tenantCode: string): string {
  return tenantCode ? `/${locale}/${tenantCode}` : '';
}

function createContext(options: {
  canonicalPathname: string;
  locale: SystemLocale;
  needsNormalization: boolean;
  routeSuffix: string;
  tenantCode: string;
}): MobileRouteContext {
  const basename = buildBasename(options.locale, options.tenantCode);
  return {
    locale: options.locale,
    tenantCode: options.tenantCode,
    routePath: options.routeSuffix || '/',
    basename,
    canonicalPathname: options.canonicalPathname,
    needsNormalization: options.needsNormalization,
  };
}

/** Parse the complete Mobile locale + tenant prefix without guessing app routes. */
export function parseMobileRoute(pathname: string): MobileRouteContext {
  const normalizedPathname = ensurePathname(pathname);
  const segments = normalizedPathname.slice(1).split('/');
  const firstSegment = segments[0] ?? '';
  const secondSegment = segments[1];

  if (isSystemLocale(firstSegment)) {
    if (!isTenantCode(secondSegment)) {
      return createContext({
        canonicalPathname: normalizedPathname,
        locale: firstSegment,
        needsNormalization: false,
        routeSuffix: '/',
        tenantCode: '',
      });
    }

    const basename = buildBasename(firstSegment, secondSegment);
    const routeSuffix = normalizedPathname.slice(basename.length);
    return createContext({
      canonicalPathname: normalizedPathname,
      locale: firstSegment,
      needsNormalization: false,
      routeSuffix,
      tenantCode: secondSegment,
    });
  }

  if (
    CANONICAL_LOCALE_TOKEN_PATTERN.test(firstSegment) &&
    isTenantCode(secondSegment)
  ) {
    const unsupportedBasename = `/${firstSegment}/${secondSegment}`;
    const routeSuffix = normalizedPathname.slice(unsupportedBasename.length);
    const basename = buildBasename(DEFAULT_SYSTEM_LOCALE, secondSegment);
    return createContext({
      canonicalPathname: `${basename}${routeSuffix}`,
      locale: DEFAULT_SYSTEM_LOCALE,
      needsNormalization: true,
      routeSuffix,
      tenantCode: secondSegment,
    });
  }

  if (isTenantCode(firstSegment)) {
    const legacyBasename = `/${firstSegment}`;
    const routeSuffix = normalizedPathname.slice(legacyBasename.length);
    const basename = buildBasename(DEFAULT_SYSTEM_LOCALE, firstSegment);
    return createContext({
      canonicalPathname: `${basename}${routeSuffix}`,
      locale: DEFAULT_SYSTEM_LOCALE,
      needsNormalization: true,
      routeSuffix,
      tenantCode: firstSegment,
    });
  }

  return createContext({
    canonicalPathname: normalizedPathname,
    locale: DEFAULT_SYSTEM_LOCALE,
    needsNormalization: false,
    routeSuffix: normalizedPathname,
    tenantCode: '',
  });
}

export function getCurrentMobileRouteContext(): MobileRouteContext {
  return parseMobileRoute(window.location.pathname);
}

export function buildCanonicalMobileHref(
  location: MobileLocationParts,
): string {
  const context = parseMobileRoute(location.pathname);
  return `${context.canonicalPathname}${location.search ?? ''}${location.hash ?? ''}`;
}

export function buildMobileAppHref(
  appHref: string,
  context: MobileRouteContext = getCurrentMobileRouteContext(),
): string {
  if (!context.basename) return appHref || '/';
  if (!appHref) return context.basename;
  return `${context.basename}${appHref.startsWith('/') ? appHref : `/${appHref}`}`;
}

export function buildMobileLocaleHref(
  locale: SystemLocale,
  location: MobileLocationParts,
): string {
  const context = parseMobileRoute(location.pathname);
  if (!context.tenantCode) {
    return `${context.canonicalPathname}${location.search ?? ''}${location.hash ?? ''}`;
  }

  const routeSuffix = context.canonicalPathname.slice(context.basename.length);
  const pathname = `${buildBasename(locale, context.tenantCode)}${routeSuffix}`;
  return `${pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

/** Normalize legacy/unsupported-locale URLs before React mounts. */
export function normalizeCurrentMobileRoute(
  location: MobileLocationParts = window.location,
  history: MobileHistory = window.history,
): MobileRouteContext {
  const context = parseMobileRoute(location.pathname);
  if (!context.needsNormalization) return context;

  history.replaceState(history.state, '', buildCanonicalMobileHref(location));
  return { ...context, needsNormalization: false };
}
